import { AsyncPipe, DecimalPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatOption, MatSelect } from '@angular/material/select'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { ChartConfiguration } from 'chart.js'
import { BaseChartDirective } from 'ng2-charts'
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, map, shareReplay, switchMap } from 'rxjs'
import { ClientsService } from '../clients.service'
import { MetricsService, SlaOrganizationBreakdown, SlaProcessingDistributionBucket } from '../metrics.service'
import { MultiFilterComponent } from '../multi-filter/multi-filter.component'
import { OrganizationsService } from '../organizations.service'
import { AppTableColumn, TableComponent } from '../table/table.component'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { UsersService } from '../users.service'
import { getColorPalette, isEqual } from '../utils'

// The picker only needs enough of an organization to identify and label it, which lets the client-scoped and
// the environment-wide sources feed the same filter.
export type SelectableOrganization = { id: number; name: string; clientName: string }

// The same "<client> / <organization>" label the support pages use. It earns its keep only for a support user,
// whose picker spans every client and would otherwise show several identically-named organizations; an admin
// never sees past their own client, so the prefix would repeat one name down the whole list.
export function describeSelectableOrganization(organization: SelectableOrganization, includeClientName: boolean): string {
  return includeClientName ? `${organization.clientName} / ${organization.name}` : organization.name
}

// Grouped by client so the shared prefix reads as a heading rather than as repetition scattered down the list.
function byClientThenOrganization(first: SelectableOrganization, second: SelectableOrganization): number {
  return first.clientName.localeCompare(second.clientName) || first.name.localeCompare(second.name)
}

const SELECTABLE_MONTH_COUNT = 12

const MONTH_NAME_KEYS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER']

export type SlaMonthOption = {
  value: string
  nameKey: string
  year: number
}

/**
 * Months are cut on UTC boundaries on the backend, so the options are built from UTC parts too. Deriving
 * them from the local date would offer a month the backend rejects for the couple of hours around a month
 * change when the two calendars disagree.
 */
export function buildSlaMonthOptions(now: Date): SlaMonthOption[] {
  return Array.from({ length: SELECTABLE_MONTH_COUNT }, (_unused, index) => {
    const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - index, 1))
    const year = monthStart.getUTCFullYear()
    const monthOfYear = monthStart.getUTCMonth() + 1
    return {
      value: `${year}-${String(monthOfYear).padStart(2, '0')}`,
      nameKey: MONTH_NAME_KEYS[monthStart.getUTCMonth()],
      year,
    }
  })
}

@Component({
  selector: 'app-sla-dashboard',
  standalone: true,
  imports: [
    AsyncPipe,
    BaseChartDirective,
    DecimalPipe,
    FormsModule,
    MatButton,
    MatFormField,
    MatIcon,
    MatLabel,
    MatOption,
    MatSelect,
    MultiFilterComponent,
    TableComponent,
    SpinnerComponent,
    ToolbarComponent,
    TranslatePipe,
  ],
  templateUrl: './sla-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sla-dashboard.component.scss',
})
export class SlaDashboardComponent {
  route = inject(ActivatedRoute)
  router = inject(Router)
  clientsService = inject(ClientsService)
  metricsService = inject(MetricsService)
  organizationsService = inject(OrganizationsService)
  usersService = inject(UsersService)
  translateService = inject(TranslateService)

  monthOptions = buildSlaMonthOptions(new Date())

  client$ = this.clientsService.getCurrentUserClient().pipe(shareReplay(1))

  currentUser$ = this.usersService.getCurrentUser().pipe(
    filter((result) => result.isSuccess),
    map((result) => result.data ?? null),
    filter((user) => user !== null),
    shareReplay(1)
  )

  isSupportUser$ = this.currentUser$.pipe(
    map((user) => user.role === 'SUPPORT'),
    shareReplay(1)
  )

  // The picker invokes its label callbacks as bare functions rather than through a binding, so the role has
  // to be readable synchronously rather than piped in from the template.
  private isSupportUser = false

  private trackRole = this.isSupportUser$.pipe(takeUntilDestroyed()).subscribe((isSupportUser) => (this.isSupportUser = isSupportUser))

  // The client endpoint returns the organizations without repeating the client on each one, so the name is
  // taken from the client itself.
  private organizationsOfClient$ = this.client$.pipe(
    filter((result) => result.isSuccess),
    map((result): SelectableOrganization[] =>
      (result.data?.organizations ?? []).map((organization) => ({
        id: organization.id,
        name: organization.name,
        clientName: result.data?.name ?? '',
      }))
    ),
    shareReplay(1)
  )

  // Support users report on any organization, not just the one their own account happens to sit under. Admins
  // stay inside their own client, which the endpoint enforces regardless of what the picker offers.
  organizations$ = this.isSupportUser$.pipe(
    switchMap((isSupportUser) =>
      isSupportUser
        ? this.organizationsService.getAllOrganizations().pipe(
            filter((result) => result.isSuccess),
            map((result): SelectableOrganization[] =>
              (result.data ?? []).map((organization) => ({
                id: organization.id,
                name: organization.name,
                clientName: organization.client.name,
              }))
            )
          )
        : this.organizationsOfClient$
    ),
    map((organizations) => [...organizations].sort(byClientThenOrganization)),
    shareReplay(1)
  )

  // Deduped because the whole parameter map is upstream: the table's own sorting is in the URL now,
  // and a sort press must not re-run the month's figures.
  private selectedOrganizationIdsFromUrl$ = this.route.queryParamMap.pipe(
    map((params) =>
      params
        .getAll('organizationIds')
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id))
    ),
    distinctUntilChanged((previous, current) => isEqual(previous, current))
  )

  // An empty selection falls back to the viewer's own client rather than to everything the picker offers: a
  // support user opening the page wants their own client's figures, not every client in the environment summed
  // into one meaningless total.
  organizationIds$ = combineLatest([this.organizations$, this.organizationsOfClient$, this.selectedOrganizationIdsFromUrl$]).pipe(
    map(([selectableOrganizations, organizationsOfClient, selectedIds]) => {
      const validSelectedIds = selectedIds.filter((id) => selectableOrganizations.some((organization) => organization.id === id))
      return validSelectedIds.length > 0 ? validSelectedIds : organizationsOfClient.map((organization) => organization.id)
    }),
    shareReplay(1)
  )

  selectedOrganizationIds$ = combineLatest([this.organizations$, this.selectedOrganizationIdsFromUrl$]).pipe(
    map(([organizations, selectedIds]) => selectedIds.filter((id) => organizations.some((organization) => organization.id === id)))
  )

  selectedMonth$ = this.route.queryParamMap.pipe(
    map((params) => {
      const month = params.get('month')
      return this.monthOptions.some((option) => option.value === month) ? month! : this.monthOptions[0].value
    }),
    distinctUntilChanged(),
    shareReplay(1)
  )

  slaMetrics$ = combineLatest([this.organizationIds$, this.selectedMonth$]).pipe(
    switchMap(([organizationIds, month]) => this.metricsService.getSlaMetrics(organizationIds, month)),
    shareReplay(1)
  )

  processingDistributionChart$ = this.slaMetrics$.pipe(
    filter((result) => result.isSuccess),
    map((result): ChartConfiguration<'bar'>['data'] => {
      const buckets = result.data?.processingDistribution ?? []
      return {
        labels: buckets.map((bucket, index) => this.describeDistributionBucket(buckets, bucket, index)),
        datasets: [
          {
            data: buckets.map((bucket) => bucket.callCount),
            label: this.translateService.instant('SLA_DASHBOARD.CALLS_AXIS'),
            backgroundColor: getColorPalette(1)[0],
          },
        ],
      }
    })
  )

  processingDistributionChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
    plugins: {
      legend: { display: false },
    },
  }

  readonly organizationColumns: AppTableColumn<SlaOrganizationBreakdown>[] = [
    {
      id: 'organizationName',
      header: 'COMMON.ORGANIZATION',
      value: (organization) => organization.organizationName,
      tone: 'strong',
      sortable: true,
      autoFit: true,
    },
    {
      id: 'completedCallCount',
      header: 'SLA_DASHBOARD.COMPLETED_CALLS',
      value: (organization) => organization.volume.completedCallCount,
      numeric: true,
      sortable: true,
    },
    {
      id: 'completedCallHours',
      header: 'SLA_DASHBOARD.TOTAL_HOURS',
      value: (organization) => Math.round(organization.volume.completedCallDurationInSeconds / 3600),
      numeric: true,
      sortable: true,
    },
    {
      id: 'erroredCallCount',
      header: 'SLA_DASHBOARD.ERRORED_CALLS',
      value: (organization) => organization.volume.erroredCallCount,
      numeric: true,
      sortable: true,
    },
    {
      id: 'erroredCallPercentage',
      header: 'SLA_DASHBOARD.ERROR_RATE',
      value: (organization) => organization.volume.erroredCallPercentage,
      display: (organization) => `${organization.volume.erroredCallPercentage.toFixed(1)} %`,
      numeric: true,
      sortable: true,
    },
    {
      id: 'medianProcessing',
      header: 'SLA_DASHBOARD.MEDIAN_PROCESSING_TIME',
      value: (organization) => organization.processing.medianInSeconds ?? 0,
      display: (organization) => this.formatMinutes(organization.processing.medianInSeconds),
      numeric: true,
      sortable: true,
    },
    {
      id: 'measuredCallCount',
      header: 'SLA_DASHBOARD.MEASURED_CALLS',
      value: (organization) => organization.processing.measuredCallCount,
      display: (organization) => `${organization.processing.measuredCallCount} / ${organization.processing.callsWithAgendaCount}`,
      numeric: true,
    },
  ]

  organizationRowId = (organization: SlaOrganizationBreakdown): string => String(organization.organizationId)

  formatMinutes(durationInSeconds: number | null): string {
    if (durationInSeconds === null) {
      return '-'
    }
    return `${Math.round(durationInSeconds / 60)} ${this.translateService.instant('SLA_DASHBOARD.MINUTES_ABBREVIATION')}`
  }

  displayOrganization = (organization: SelectableOrganization): string => describeSelectableOrganization(organization, this.isSupportUser)

  // The collapsed summary drops the client prefix once every selected organization shares one, since repeating
  // it on each entry only makes the chip longer without telling the reader anything.
  displaySelectedOrganizations(organizations: SelectableOrganization[]) {
    return (ids: number[]) => {
      const selected = organizations.filter((organization) => ids.includes(organization.id))
      const spansSeveralClients = new Set(selected.map((organization) => organization.clientName)).size > 1
      return selected.map((organization) => describeSelectableOrganization(organization, this.isSupportUser && spansSeveralClients)).join(', ')
    }
  }

  isExporting = false

  async exportToPdf() {
    // Guarded because rendering runs a browser server side: a double click would launch a second one for no
    // benefit, in a container that is already tight on memory.
    if (this.isExporting) {
      return
    }
    this.isExporting = true
    try {
      // Resolving the selection is inside the try as well: if that stream fails, the flag still has to clear
      // or the button stays disabled for the rest of the visit.
      const [organizationIds, month] = await firstValueFrom(combineLatest([this.organizationIds$, this.selectedMonth$]))
      await firstValueFrom(this.metricsService.exportSlaMetrics(organizationIds, month), { defaultValue: undefined })
    } finally {
      this.isExporting = false
    }
  }

  async onMonthChange(month: string) {
    await this.router.navigate([], { queryParams: { month }, queryParamsHandling: 'merge', relativeTo: this.route })
  }

  async onOrganizationChange(selectedIds: number[]) {
    await this.router.navigate([], {
      queryParams: selectedIds.length ? { organizationIds: selectedIds } : { organizationIds: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  async onOrganizationClear() {
    await this.router.navigate([], {
      queryParams: { organizationIds: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  private describeDistributionBucket(buckets: SlaProcessingDistributionBucket[], bucket: SlaProcessingDistributionBucket, index: number): string {
    const lowerBound = index === 0 ? null : buckets[index - 1].upToSeconds
    if (lowerBound === null) {
      return `< ${this.formatBoundary(bucket.upToSeconds)}`
    }
    if (bucket.upToSeconds === null) {
      return `> ${this.formatBoundary(lowerBound)}`
    }
    return `${this.formatBoundary(lowerBound)} – ${this.formatBoundary(bucket.upToSeconds)}`
  }

  private formatBoundary(boundaryInSeconds: number | null): string {
    if (boundaryInSeconds === null) {
      return ''
    }
    if (boundaryInSeconds < 3600) {
      return `${boundaryInSeconds / 60} ${this.translateService.instant('SLA_DASHBOARD.MINUTES_ABBREVIATION')}`
    }
    return `${boundaryInSeconds / 3600} ${this.translateService.instant('SLA_DASHBOARD.HOURS_ABBREVIATION')}`
  }
}
